/**
 * **추적 등록 의사** — 「이것을 걸고 싶다」를 추적 관리 화면(`/service/tracking`)으로 넘기는 옛 주소 한 벌.
 *
 * ```
 *   옛 링크·사이드바 「세트 만들기」 ─(?add=…)─▶ 추적 관리 ─▶ 추적 등록 창이 그 값으로 열린다
 * ```
 *
 * 🔑 **새 문은 추적 등록 창이다**(`TrackRegisterDialog`) — 화면들은 이 주소를 짓지 않고 창을 직접 연다.
 *   이 파일은 이미 나가 있는 링크와 사이드바의 한 줄을 받기 위해 남는다.
 * ⛔ 들고 왔다고 **자동으로 걸지 마라** — 창이 열리고, 고치고 누르는 것은 사람이 한다.
 * ⚠️ 주소에는 **공개된 말**만 싣는다 — 브랜드 이름 · 검색어 · 계정 아이디. 사람을 가리키는 값은 싣지 않는다.
 */

import type { TrackRegisterInitial } from './track-register';

/** 추적 관리의 어느 구역에 거는가 — 그 화면의 등록 갈래와 같은 말이다 */
export type TrackIntentKind = 'brandKeyword' | 'brandAccount' | 'targetKeyword';

/** 넘기는 값 한 벌 — 갈래마다 쓰는 칸이 다르다 */
export type TrackIntent = {
  kind: TrackIntentKind;
  /** 걸 말 — 브랜드 키워드 · 타겟 키워드 */
  keyword?: string;
  /** 계정의 채널 */
  platform?: string;
  /** 계정 아이디 */
  username?: string;
  /** 수집 세트 이름 — 없으면 그 화면이 말이나 계정에서 짓는다 */
  name?: string;
};

/** 주소에 싣는 칸 이름 — 짧게 둔다(주소가 화면에 보인다) */
const PARAM = { kind: 'add', keyword: 'q', platform: 'ch', username: 'ac', name: 'nm' } as const;

const KINDS: readonly TrackIntentKind[] = ['brandKeyword', 'brandAccount', 'targetKeyword'];

/**
 * 추적 관리로 가는 주소를 짓는다.
 *
 * @param base 추적 관리 화면의 경로(`paths.service.status`)
 * @param intent 걸고 싶은 것 — 빈 값은 싣지 않는다
 */
export function toTrackIntentHref(base: string, intent: TrackIntent): string {
  const parts: string[] = [`${PARAM.kind}=${encodeURIComponent(intent.kind)}`];
  const put = (key: string, value: string | undefined) => {
    const text = String(value ?? '').trim();
    if (text) parts.push(`${key}=${encodeURIComponent(text)}`);
  };
  put(PARAM.keyword, intent.keyword);
  put(PARAM.platform, intent.platform);
  put(PARAM.username, intent.username);
  put(PARAM.name, intent.name);
  return `${base}?${parts.join('&')}`;
}

/**
 * 주소에서 의사를 읽는다.
 *
 * @param get 칸 하나를 읽어 주는 것(`searchParams.get`)
 * @returns 모르는 갈래거나 갈래가 없으면 `null` — 그때 화면은 평소대로 연다
 */
export function readTrackIntent(get: (key: string) => string | null): TrackIntent | null {
  const raw = String(get(PARAM.kind) ?? '').trim();
  const kind = KINDS.find((one) => one === raw);
  if (!kind) return null;
  const read = (key: string) => String(get(key) ?? '').trim();
  return {
    kind,
    keyword: read(PARAM.keyword),
    platform: read(PARAM.platform),
    username: read(PARAM.username),
    name: read(PARAM.name),
  };
}

/**
 * 옛 의사를 추적 등록 창의 처음 값으로 옮긴다.
 *
 * 브랜드 키워드 → 우리 브랜드 키워드 · 자사 계정 → 계정(우리 계정) · 타겟 키워드는 계정 아이디가 있으면
 * 계정(남의 계정), 아니면 키워드.
 */
export function toTrackRegisterInitial(intent: TrackIntent): TrackRegisterInitial {
  if (intent.kind === 'brandKeyword') return { type: 'brand', value: intent.keyword };
  if (intent.kind === 'brandAccount') {
    return { type: 'account', accountOwner: 'own', platform: intent.platform, username: intent.username };
  }
  if (String(intent.username ?? '').trim()) {
    return { type: 'account', accountOwner: 'other', platform: intent.platform, username: intent.username };
  }
  return { type: 'keyword', value: intent.keyword };
}
